// encodeView() — RFC-102 P3 + P4 + P7.
// Converts a ViewerState into a URL hash fragment of the form
//   #view=<urlbase64>      (editable tier)
//   #viewro=<urlbase64>    (read-only tier)
// or throws when the byte budget is exceeded.

import { encodeUtf8ToUrlBase64, utf8ByteLength } from './base64';
import {
  HASH_PREFIX_EDITABLE,
  HASH_PREFIX_READONLY,
  URL_PAYLOAD_HARD_LIMIT_BYTES,
  URL_PAYLOAD_WARN_BYTES,
  URL_STATE_SCHEMA_VERSION,
  type CompactScenario,
  type Scenario,
  type UrlViewState,
  type ViewerState,
  type ViewTime,
} from './schema';

export interface EncodeOptions {
  /** When true, produces the read-only `#viewro=` tier (RFC-102 P4). */
  readOnly?: boolean;
  /**
   * If supplied, called when the encoded payload exceeds
   * URL_PAYLOAD_WARN_BYTES (1500). Lets the host surface a "shorten me"
   * hint to the user before hitting the hard limit at 1800.
   */
  onLargePayload?: (bytes: number) => void;
}

export class UrlPayloadTooLargeError extends Error {
  readonly bytes: number;
  constructor(bytes: number) {
    super(
      `URL-state payload is ${bytes} bytes; exceeds RFC-102 P3 hard limit of ${URL_PAYLOAD_HARD_LIMIT_BYTES} bytes. Use a server-side view ticket follow-up or trim the scenario set.`,
    );
    this.name = 'UrlPayloadTooLargeError';
    this.bytes = bytes;
  }
}

function timeToString(t: ViewTime | undefined): string | null | undefined {
  if (t === undefined) return undefined;
  if (t === null) return null;
  return t.toISOString();
}

function compactScenario(s: Scenario): CompactScenario {
  const c: CompactScenario = {
    id: s.id,
    lab: s.label,
    lids: [...s.layerIds],
  };
  if (s.pinnedTime !== undefined) {
    c.pt = timeToString(s.pinnedTime);
  }
  if (s.layerOverrides && Object.keys(s.layerOverrides).length > 0) {
    const lov: NonNullable<CompactScenario['lov']> = {};
    for (const [layerId, override] of Object.entries(s.layerOverrides)) {
      const entry: { st?: unknown; vis?: { mn?: number; mx?: number } } = {};
      if (override.style !== undefined) entry.st = override.style;
      if (override.visibility !== undefined) {
        entry.vis = {};
        if (override.visibility.minZoom !== undefined) entry.vis.mn = override.visibility.minZoom;
        if (override.visibility.maxZoom !== undefined) entry.vis.mx = override.visibility.maxZoom;
      }
      lov[layerId] = entry;
    }
    c.lov = lov;
  }
  if (s.notes !== undefined) c.n = s.notes;
  return c;
}

/**
 * Convert a ViewerState into the compact UrlViewState shape (RFC-102 P3).
 * Pure function; useful for tests and for size-budget checks before encoding.
 */
export function toUrlViewState(state: ViewerState): UrlViewState {
  const u: UrlViewState = { v: URL_STATE_SCHEMA_VERSION };
  if (state.view) {
    if (state.view.center !== undefined) u.c = [state.view.center[0], state.view.center[1]];
    if (state.view.zoom !== undefined) u.z = state.view.zoom;
    if (state.view.heading !== undefined) u.h = state.view.heading;
    if (state.view.pitch !== undefined) u.p = state.view.pitch;
  }
  const t = timeToString(state.time);
  if (t !== undefined) u.t = t;
  if (state.visibleLayerIds !== undefined) u.l = [...state.visibleLayerIds];
  if (state.primaryEngine !== undefined) u.e = state.primaryEngine;
  if (state.scenarios !== undefined && state.scenarios.length > 0) {
    u.sc = state.scenarios.map(compactScenario);
  }
  if (state.activeScenarioId !== undefined) u.sa = state.activeScenarioId;
  if (state.compareWithScenarioId !== undefined) u.cmp = state.compareWithScenarioId;
  if (state.orgAlias !== undefined) u.o = state.orgAlias;
  if (state.featureFlags !== undefined && Object.keys(state.featureFlags).length > 0) {
    u.fl = { ...state.featureFlags };
  }
  return u;
}

export function encodeView(state: ViewerState, options: EncodeOptions = {}): string {
  const compact = toUrlViewState(state);
  const json = JSON.stringify(compact);
  const b64 = encodeUtf8ToUrlBase64(json);
  const prefix = options.readOnly ? HASH_PREFIX_READONLY : HASH_PREFIX_EDITABLE;
  const out = prefix + b64;
  const bytes = utf8ByteLength(out);
  if (bytes > URL_PAYLOAD_HARD_LIMIT_BYTES) {
    throw new UrlPayloadTooLargeError(bytes);
  }
  if (bytes > URL_PAYLOAD_WARN_BYTES) {
    options.onLargePayload?.(bytes);
  }
  return out;
}

/** Byte length of an encoded ViewerState (without throwing on overflow). */
export function measureEncodedBytes(state: ViewerState, options: { readOnly?: boolean } = {}): number {
  const compact = toUrlViewState(state);
  const json = JSON.stringify(compact);
  const b64 = encodeUtf8ToUrlBase64(json);
  const prefix = options.readOnly ? HASH_PREFIX_READONLY : HASH_PREFIX_EDITABLE;
  return utf8ByteLength(prefix + b64);
}
